import React, { PropsWithChildren, useEffect } from "react";
import { useTranslation } from "i18n";
import { useSessionStorage } from "usehooks-ts";
import {
  INVALID_GRANT_ERROR_CODE,
  OIDC_BROADCAST_UPDATE_USER,
  OIDC_REFRESH_TOKEN_LOCK_ID,
  OIDC_BROADCAST_SIGNIN_USER,
  oidcLogout,
  POST_LOGIN_REDIRECT_KEY,
  SIGN_IN_CALLBACK_URL,
  SL_MEMBER_GROUP,
  useAuth,
  useAutoSignin,
  useAxiosTokenRefresh,
  useCheckAndSetPortalCookie,
  UserAttributes,
  oidcTabId,
  oidcBroadcastChannel,
} from "shared-auth";
import { AuthErrorCode } from "shared-types";
import { Spinner } from "../../../../Atoms/Spinner/Spinner";
import CommonErrorPopup from "../../../CommonErrorPopup";
import { userManager } from "../AuthProvider";

const Authenticator = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const [, setPostLoginRedirect] = useSessionStorage(
    POST_LOGIN_REDIRECT_KEY,
    ""
  );
  const auth = useAuth();

  const { isLoading, isAuthenticated, error } = useAutoSignin();

  useEffect(() => {
    if (auth.activeNavigator === "signinRedirect") {
      setPostLoginRedirect(window.location.href);
    }
  }, [auth.activeNavigator, setPostLoginRedirect]);

  useEffect(() => {
    oidcBroadcastChannel.onmessage = async (e) => {
      if (
        e.data.type === OIDC_BROADCAST_UPDATE_USER &&
        e.data.sender !== oidcTabId &&
        e.data.user
      ) {
        await auth.events.load(e.data.user);
      }

      if (
        e.data.type === OIDC_BROADCAST_SIGNIN_USER &&
        e.data.sender !== oidcTabId
      ) {
        await userManager.getUser();
      }
    };
  }, []);

  useEffect(() => {
    async function refreshToken() {
      await navigator.locks.request(
        OIDC_REFRESH_TOKEN_LOCK_ID,
        { mode: "exclusive", ifAvailable: true },
        async (lock) => {
          if (!lock) return;

          const user = await auth.signinSilent();

          oidcBroadcastChannel.postMessage({
            type: OIDC_BROADCAST_UPDATE_USER,
            user,
            sender: oidcTabId,
          });
        }
      );
    }

    auth.events.addAccessTokenExpiring(refreshToken);

    return () => auth.events.removeAccessTokenExpiring(refreshToken);
  }, []);

  useAxiosTokenRefresh();

  useCheckAndSetPortalCookie();

  if (
    (auth.activeNavigator !== "signinSilent" && isLoading) ||
    window.location.pathname.includes(SIGN_IN_CALLBACK_URL)
  ) {
    return <Spinner />;
  }

  //stub for cognito users without groups
  //to be removed when cognito users are migrated to have groups SL-8055
  const profileCheckerUser =
    auth.user?.profile[UserAttributes.IdentityProvider] === "cognito";

  const userHasEmptyGroup =
    isAuthenticated &&
    !profileCheckerUser &&
    !auth.user?.profile[SL_MEMBER_GROUP];

  if (!isAuthenticated || error || userHasEmptyGroup) {
    const invalidGrant = error?.message === INVALID_GRANT_ERROR_CODE;
    const refreshFlow = () =>
      invalidGrant ? auth.signinRedirect() : window.location.reload();
    const closeButton = userHasEmptyGroup ? () => oidcLogout() : refreshFlow;

    return (
      <CommonErrorPopup
        errorStatusCode={440}
        errorCode={
          userHasEmptyGroup
            ? AuthErrorCode.EmptyGroup
            : AuthErrorCode.SessionExpired
        }
        popupProps={{
          close: closeButton,
        }}
        alertMessageProps={{
          buttons: [
            {
              name: t("common:button.label.signin"),
              type: "primary",
              click: closeButton,
            },
          ],
        }}
      />
    );
  }

  return <>{children}</>;
};

export default Authenticator;
